import { Landmark, Smartphone } from 'lucide-react';
import { TextField } from '@/components/text-field';
import type { PayoutRequest } from '@/features/onboarding/types';

/**
 * The payout form, shared by the onboarding step and the post-onboarding editor.
 *
 * Extracted rather than written twice: the two screens have different framing — one is "set this
 * up", the other is "you may change this once a month" — but the fields, the validation and the
 * IFSC upper-casing must be identical, or a vendor could pass one screen and fail the other with
 * the same keystrokes.
 */

export type PayoutForm = {
  method: 'BANK_ACCOUNT' | 'UPI';
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  upi_id: string;
};

export const EMPTY_PAYOUT_FORM: PayoutForm = {
  // UPI first because it is the shorter path and what most small vendors actually use.
  method: 'UPI',
  account_holder_name: '',
  account_number: '',
  ifsc_code: '',
  upi_id: '',
};

// Kept in step with the server-side regexes in vendor.validators.ts. Duplicated on purpose: the
// server is the authority, but a vendor should not have to post a request to be told their IFSC
// is the wrong shape.
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const UPI_REGEX = /^[\w.\-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/;

export function validate_payout(form: PayoutForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.method === 'UPI') {
    if (!UPI_REGEX.test(form.upi_id.trim())) {
      errors.upi_id = 'Enter a UPI ID in the form name@bank, e.g. rahul@oksbi.';
    }
    return errors;
  }
  if (form.account_holder_name.trim().length < 2) {
    errors.account_holder_name = 'Enter the name exactly as it appears on the account.';
  }
  if (!/^[0-9]{6,20}$/.test(form.account_number.trim())) {
    errors.account_number = 'Account number must be 6 to 20 digits.';
  }
  if (!IFSC_REGEX.test(form.ifsc_code.trim().toUpperCase())) {
    errors.ifsc_code = 'Enter a valid 11-character IFSC, e.g. HDFC0001234.';
  }
  return errors;
}

/** Narrow the flat form down to the discriminated union the API expects. */
export function payout_payload(form: PayoutForm): PayoutRequest {
  if (form.method === 'UPI') {
    return { method: 'UPI', upi_id: form.upi_id.trim() };
  }
  return {
    method: 'BANK_ACCOUNT',
    account_holder_name: form.account_holder_name.trim(),
    account_number: form.account_number.trim(),
    ifsc_code: form.ifsc_code.trim().toUpperCase(),
  };
}

const METHODS = [
  { value: 'UPI' as const, label: 'UPI', hint: 'Fastest — just your UPI ID', icon: Smartphone },
  { value: 'BANK_ACCOUNT' as const, label: 'Bank account', hint: 'Account number + IFSC', icon: Landmark },
];

export function PayoutFields({
  form,
  field_errors,
  on_change,
  disabled,
}: {
  form: PayoutForm;
  field_errors: Record<string, string>;
  on_change: <K extends keyof PayoutForm>(key: K, value: PayoutForm[K]) => void;
  disabled?: boolean;
}) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">How should we pay you?</span>
        <div className="grid gap-3 sm:grid-cols-2">
          {METHODS.map((m) => {
            const selected = form.method === m.value;
            const Icon = m.icon;
            return (
              <button
                key={m.value}
                type="button"
                disabled={disabled}
                onClick={() => on_change('method', m.value)}
                aria-pressed={selected}
                data-testid={`payout-method-${m.value}`}
                className={[
                  'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border hover:border-primary/40',
                  disabled ? 'cursor-not-allowed opacity-60' : '',
                ].join(' ')}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="flex flex-col">
                  <span className="text-sm font-medium">{m.label}</span>
                  <span className="text-xs text-muted-foreground">{m.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {form.method === 'UPI' ? (
        <TextField
          label="UPI ID"
          name="upi_id"
          data-testid="field-upi-id"
          placeholder="rahul@oksbi"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={form.upi_id}
          error={field_errors.upi_id}
          hint="Double-check this. Money sent to a mistyped UPI ID goes to whoever owns it."
          onChange={(e) => on_change('upi_id', e.target.value)}
        />
      ) : (
        <>
          <TextField
            label="Account holder name"
            name="account_holder_name"
            data-testid="field-account-holder-name"
            placeholder="Rahul Sharma"
            disabled={disabled}
            value={form.account_holder_name}
            error={field_errors.account_holder_name}
            hint="Exactly as printed on the passbook — banks reject transfers on a name mismatch."
            onChange={(e) => on_change('account_holder_name', e.target.value)}
          />
          <TextField
            label="Account number"
            name="account_number"
            data-testid="field-account-number"
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            value={form.account_number}
            error={field_errors.account_number}
            onChange={(e) => on_change('account_number', e.target.value.replace(/[^0-9]/g, ''))}
          />
          <TextField
            label="IFSC code"
            name="ifsc_code"
            data-testid="field-ifsc-code"
            placeholder="HDFC0001234"
            autoComplete="off"
            spellCheck={false}
            disabled={disabled}
            value={form.ifsc_code}
            error={field_errors.ifsc_code}
            hint="11 characters, printed on your cheque book."
            // Upper-cased as they type rather than on submit, so what they see is what is sent.
            onChange={(e) => on_change('ifsc_code', e.target.value.toUpperCase())}
          />
        </>
      )}
    </>
  );
}

/** One line describing a saved account, for a summary row. */
export function describe_payout_view(v: {
  method: string;
  account_number_last4: string | null;
  ifsc_code: string | null;
  upi_id_display: string | null;
}): string {
  return v.method === 'UPI'
    ? `UPI · ${v.upi_id_display ?? 'hidden'}`
    : `Bank · ****${v.account_number_last4 ?? '????'} · ${v.ifsc_code ?? ''}`;
}
